/**
 * Sconto ancillary in emissione RA (US-NTWRA-001 / LPP-22289, RDT §2.1(9),(11)).
 *
 * Regole (RDT §2.1(11), RF-05):
 *  - box sconto % con menù a tendina per singolo item, entro un range configurato
 *    centralmente (es. 5–20%), applicabile a tutti i segmenti;
 *  - SOLO vendite al banco (VB-04): il controllo vive esclusivamente nel flusso
 *    `create-ra` del ramo network (desk DMS), mai online;
 *  - gli accessori con `flag_discount_applicable=false` NON mostrano il campo sconto
 *    (flag owner PRC, letto dall'offerta);
 *  - la percentuale scelta è visibile in fattura e sull'RA al cliente (transita nel
 *    body verso il BE proxy → NTW → PRC come `applyDeskDiscount`);
 *  - nessun cap oltre il range (D-BU-FNN-4).
 *
 * Fallback range (documentato): quando il range non è configurato/disponibile lato
 * dati (owner PRC), si applica {@link DEFAULT_ANCILLARY_DISCOUNT_RANGE} = 5–20% step 5,
 * coerente con l'esempio del RDT. In questo modo l'operatore può comunque scontare
 * entro un intervallo prudenziale invece di restare senza controllo.
 */

export interface IAncillaryDiscountRange {
	/** percentuale minima selezionabile (inclusa), es. 5 */
	min: number;
	/** percentuale massima selezionabile (inclusa), es. 20 */
	max: number;
	/** passo tra due valori consecutivi, es. 5 */
	step: number;
}

/** Range di fallback usato quando il range non è configurato/disponibile lato dati. */
export const DEFAULT_ANCILLARY_DISCOUNT_RANGE: IAncillaryDiscountRange = {
	min: 5,
	max: 20,
	step: 5,
};

/**
 * Normalizza un range potenzialmente incompleto/incoerente proveniente dai dati,
 * applicando il fallback dove necessario. Garantisce sempre un range valido
 * (min>0, max>=min, step>0).
 */
export const resolveDiscountRange = (range?: Partial<IAncillaryDiscountRange> | null): IAncillaryDiscountRange => {
	const min = Number(range?.min);
	const max = Number(range?.max);
	const step = Number(range?.step);

	const safeStep = Number.isFinite(step) && step > 0 ? step : DEFAULT_ANCILLARY_DISCOUNT_RANGE.step;
	const safeMin = Number.isFinite(min) && min > 0 ? min : DEFAULT_ANCILLARY_DISCOUNT_RANGE.min;
	const safeMax = Number.isFinite(max) && max >= safeMin ? max : Math.max(safeMin, DEFAULT_ANCILLARY_DISCOUNT_RANGE.max);

	return {min: safeMin, max: safeMax, step: safeStep};
};

/**
 * Converte il range di governance desk-discount (in **centesimi di punto**: 250 = 2,5%, unità
 * canonica di `discountMin`/`discountMax` su PRC) nel range in punti percentuali interi usato
 * dalla tendina, arrotondando verso l'**interno** del range.
 *
 * Requisito (aggiornato): la tendina offre percentuali a **passo 5** (0,5,10,15,20,...). Si
 * selezionano SOLO i multipli di 5 **interni** al range di governance: minimo arrotondato verso
 * l'alto al multiplo di 5 (`Math.ceil(min/5)*5`), massimo verso il basso (`Math.floor(max/5)*5`).
 * L'arrotondamento verso l'interno garantisce che ogni valore offerto sia sempre dentro [min,max]
 * — la validazione backend è **inclusiva agli estremi** (PRC `DeskDiscountApplier.resolveValidatedPercentage`
 * e `...ForProfiles`) — evitando l'overshoot che generava `400 DISCOUNT_OUT_OF_RANGE`. La
 * configurazione governance (min/max) è a sua volta vincolata a passo 5, quindi in pratica gli
 * estremi coincidono già con multipli di 5.
 *
 * @returns `null` quando nessuna percentuale intera è ammessa — range interamente contenuto in
 * meno di un punto (es. 2,5–2,9%) o minimo che arrotonda a 0 — oppure valori non utilizzabili.
 * Il chiamante ricade allora sul fallback di {@link resolveDiscountRange}.
 */
export const resolveGovernanceRange = (
	discountMin?: number | null,
	discountMax?: number | null
): IAncillaryDiscountRange | null => {
	const minBp = Number(discountMin);
	const maxBp = Number(discountMax);
	if (!Number.isFinite(minBp) || !Number.isFinite(maxBp)) {
		return null;
	}

	// Requisito aggiornato: passo 5 (0,5,10,15,20,...). Si offrono SOLO i multipli di 5 interni
	// al range di governance: minimo arrotondato verso l'alto al multiplo di 5, massimo verso il
	// basso. Cosi' ogni valore selezionabile e' sempre dentro [min,max] (validazione PRC inclusiva)
	// e non si ripresenta l'overshoot che generava 400 DISCOUNT_OUT_OF_RANGE.
	const min = Math.ceil(minBp / 100 / 5) * 5;
	const max = Math.floor(maxBp / 100 / 5) * 5;
	if (min <= 0 || max < min) {
		return null;
	}

	return {min, max, step: 5};
};

/**
 * Indica se un accessorio è scontabile. Il campo `flag_discount_applicable` è owner
 * PRC: solo `false` esplicito nasconde il controllo (default: scontabile).
 */
export const isDiscountApplicable = (ancillary?: {flagDiscountApplicable?: boolean | null} | null): boolean => {
	return ancillary?.flagDiscountApplicable !== false;
};

/**
 * Costruisce le opzioni della tendina sconto entro il range (fallback incluso).
 * NON include lo 0: l'assenza di sconto è rappresentata dall'opzione "nessuno sconto"
 * (valore `null`) gestita dal componente. I valori sono deduplicati e ordinati.
 */
export const buildDiscountOptions = (range?: Partial<IAncillaryDiscountRange> | null): number[] => {
	const {min, max, step} = resolveDiscountRange(range);
	const options: number[] = [];
	for (let value = min; value <= max; value += step) {
		options.push(Number(value.toFixed(2)));
	}
	// garantisce almeno un'opzione anche con range degenere (min===max)
	if (options.length === 0) {
		options.push(min);
	}
	return options;
};

/**
 * Valida che una percentuale sia interna al range risolto. Usata per non fatturare
 * sconti fuori range (difesa lato FE prima del passthrough al BE).
 */
export const isDiscountWithinRange = (
	percentage: number | null | undefined,
	range?: Partial<IAncillaryDiscountRange> | null
): boolean => {
	if (percentage === null || percentage === undefined) {
		return true; // nessuno sconto è sempre valido
	}
	if (!Number.isFinite(percentage)) {
		return false;
	}
	const {min, max} = resolveDiscountRange(range);
	return percentage >= min && percentage <= max;
};
